/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { pushEcommerceEvent, pushEvent, type EcommerceItem } from '../data-layer';
import { getPageContext } from '../page-context';
import { buildBasketItem, buildEcommerceItem, type ProductLike } from './item-shape';
import { createLogger } from '@/lib/logger';
import type { ShopperBasketsV2 } from '@/scapi';

type ProductItem = ShopperBasketsV2.schemas['ProductItem'];

// ─── page_view ───────────────────────────────────────────────────────────────

const logger = createLogger();

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/**
 * Strips UTM parameters from URL path for cleaner page tracking (legacy behavior).
 * Returns the original path unchanged if URL parsing fails (e.g. malformed input).
 */
function stripUtmParams(path: string): string {
    try {
        const url = new URL(path, typeof window !== 'undefined' ? window.location.origin : 'https://example.com');
        UTM_PARAMS.forEach((p) => url.searchParams.delete(p));
        return url.pathname + url.search;
    } catch (err) {
        logger.warn('[GTM] stripUtmParams: unable to parse path, returning as-is', {
            err,
            path,
        });
        return path;
    }
}

export interface PageViewData {
    pageTitle?: string;
    page: string;
    referralPage?: string;
    items?: EcommerceItem[];
}

/**
 * Pushes virtual_pageview event to dataLayer.
 * Use on route changes; include ecommerce items when on product or category pages.
 * Uses event: "nonInteraction" for legacy GTM compatibility (passive page load).
 */
export function pushPageView(data: PageViewData): void {
    const page = stripUtmParams(data.page);
    const pageContext = getPageContext();
    pushEcommerceEvent(
        'virtual_pageview',
        data.items !== undefined ? { items: data.items } : {},
        {
            element_id: 'sitewide',
            page_title: data.pageTitle ?? pageContext.page_title,
            page,
            referral_page: data.referralPage ?? pageContext.referral_page,
        },
        'nonInteraction'
    );
}

// ─── session_start ───────────────────────────────────────────────────────────

const SESSION_STARTED_KEY = 'gtm_session_started';

/** Mirrors legacy Util.js `getDeviceName` (line 501). */
function getDeviceInfo(ua: string): { deviceName: string; browserName: string } {
    let deviceName = '';
    let browserName = '';

    if (/android/i.test(ua)) deviceName = 'Android Device';
    else if (/iPad|iPhone|iPod/.test(ua)) deviceName = 'iOS Device';
    else if (/windows phone/i.test(ua)) deviceName = 'Windows Phone';
    else if (/macintosh|mac os x/i.test(ua)) deviceName = 'Mac';
    else if (/windows nt/i.test(ua)) deviceName = 'Windows PC';
    else if (/linux/i.test(ua)) deviceName = 'Linux PC';

    if (/edg/i.test(ua)) browserName = 'Edge';
    else if (/opr|opera/i.test(ua)) browserName = 'Opera';
    else if (/crios|crmo|chrome/i.test(ua)) browserName = 'Chrome';
    else if (/firefox|iceweasel|fxios/i.test(ua)) browserName = 'Firefox';
    else if (/safari/i.test(ua)) browserName = 'Safari';
    else if (/msie|trident/i.test(ua)) browserName = 'Internet Explorer';

    return { deviceName, browserName };
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    if (typeof window === 'undefined') return 'desktop';
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
}

/** Read a single cookie by name from document.cookie. */
function readCookie(name: string): string {
    if (typeof document === 'undefined') return '';
    for (const raw of document.cookie.split(';')) {
        const eq = raw.indexOf('=');
        if (eq < 0) continue;
        const key = raw.slice(0, eq).trim();
        if (key !== name) continue;
        try {
            return decodeURIComponent(raw.slice(eq + 1).trim());
        } catch {
            return raw.slice(eq + 1).trim();
        }
    }
    return '';
}

/**
 * Returns the UTC offset as "GMT±HHMM" (e.g. "GMT-0500"), matching the
 * legacy site's `Intl.DateTimeFormat().resolvedOptions().timeZone` display.
 */
function getTimezoneOffset(): string {
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    return `GMT${sign}${hh}${mm}`;
}

export interface SessionStartData {
    isMember: boolean;
    currencyCode: string;
    locale: string;
}

/**
 * Pushes initial_page event to dataLayer once per session.
 * Uses sessionStorage to detect new sessions. Safe to call multiple times — only fires once.
 *
 * Shape mirrors legacy `Org_TagManager.js` / `Util.js` `sessionUserDetails` +
 * `sessionPageDetails` (Util.js lines 540–660). Server-only fields (ip_address,
 * geo, hashed emails, cdp_bsin) are emitted as empty strings — same as legacy
 * when data is unavailable — so GTM tag rules that check for field presence
 * still work correctly.
 */
export function pushSessionStart(data: SessionStartData): boolean {
    if (typeof window === 'undefined') return false;

    try {
        if (sessionStorage.getItem(SESSION_STARTED_KEY)) return false;
    } catch {
        return false;
    }

    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const { deviceName, browserName } = getDeviceInfo(ua);
    const deviceType = getDeviceType();

    const fbp = readCookie('_fbp');
    const fbc = readCookie('_fbc');
    const ga = readCookie('_ga');

    // utm_journey always emits — all 5 keys present with empty strings when
    // no UTM params are on the URL, matching legacy behaviour. utm_referrer
    // uses document.referrer (legacy column name, not utm_term).
    const params = new URLSearchParams(window.location.search);
    const utmJourney = [
        {
            utm_campaign: params.get('utm_campaign') ?? '',
            utm_medium: params.get('utm_medium') ?? '',
            utm_source: params.get('utm_source') ?? '',
            utm_content: params.get('utm_content') ?? '',
            utm_referrer: typeof document !== 'undefined' ? document.referrer : '',
        },
    ];

    pushEvent({
        event: 'nonInteraction',
        event_name: 'initial_page',
        element_id: 'sitewide',
        created: Math.round(Date.now() / 1000),
        v2: {
            user: {
                original_client_id: '',
                master_client_id: '',
                clientId: '',
                isAgent: '',
                browser: browserName,
                is_member: data.isMember,
                is_new_user: '',
                useragent: ua,
                ip_address: '',
                deviceType,
                device: deviceName,
                utm_journey: utmJourney,
                hashedEmailSHA1: '',
                hashedEmailSHA2: '',
                anonymous_hashEmailSha2: '',
                cdp_bsin: '',
                _fbp: fbp,
                _fbc: fbc,
                _ga: ga,
                timezone: getTimezoneOffset(),
                dma: '',
                country: '',
                state: '',
                city: '',
                postal_code: '',
                private_mode: '',
                device_finger_print: '',
            },
            page: {
                path: window.location.pathname,
                title: document.title,
                referral_page: document.referrer,
                documentLocation: window.location.href,
                deploymentId: '',
                release_candidate: '',
                environmentName: '',
                currencyCode: data.currencyCode,
                screen_resolution: `${screen.width}x${screen.height}`,
                language: data.locale,
            },
        },
    });

    try {
        sessionStorage.setItem(SESSION_STARTED_KEY, '1');
    } catch {
        // sessionStorage unavailable (private browsing edge case)
    }

    return true;
}

// ─── zip_map_fields ──────────────────────────────────────────────────────────

export interface ZipMapFieldsData {
    /**
     * Ashley RDC id — legacy `session.custom.rdcId`. Populated by the
     * personalization adapter on the new stack (once wired). Note the
     * legacy JSON key uses `RCID` (all-caps) — we preserve that spelling for
     * downstream GTM parity even though the SFRA session var is `rdcId`.
     */
    rcid?: string;
    /**
     * Ashley price zone — legacy `session.custom.priceZone` with a fallback
     * to `'AFHSDPG'`. On storefront-next we get this from
     * `useDeliveryZip().priceZone`. Callers should apply the same fallback
     * (`AFHSDPG`) when the ZIP is unmapped; here we accept whatever the
     * caller resolved.
     */
    priceZone?: string;
    /** Fulfiller display name — legacy `session.custom.fulfillerName`. */
    fulfillerName?: string;
    /** Market zone — legacy `session.custom.marketZone`. */
    marketZone?: string;
    /** State code (US 2-letter) — legacy `session.custom.stateCode`. */
    stateCode?: string;
}

/**
 * Pushes the Ashley custom `zipMapFields` block onto the dataLayer.
 *
 * All fields are optional; the push always fires (matching legacy which
 * emitted empty-string placeholders for absent values). We differ from
 * legacy in one respect: absent values are OMITTED from the payload rather
 * than emitted as `''`. Downstream GTM tags that were reading empty strings
 * will now read `undefined` for those keys — the ticket's "no fabricated
 * values" rule takes precedence over strict wire compatibility here.
 */
export function pushZipMapFields(data: ZipMapFieldsData): void {
    const zipMapFields: Record<string, unknown> = {};
    if (data.rcid) zipMapFields.RCID = data.rcid;
    if (data.priceZone) zipMapFields.PriceZone = data.priceZone;
    if (data.fulfillerName) zipMapFields.fulfillerName = data.fulfillerName;
    if (data.marketZone) zipMapFields.marketZone = data.marketZone;
    if (data.stateCode) zipMapFields.stateCode = data.stateCode;

    // Skip the push entirely when we have nothing to say — the empty
    // `{ zipMapFields: {} }` push would only pollute the dataLayer and clear
    // out any prior zipMapFields state that a downstream tag was reading.
    if (Object.keys(zipMapFields).length === 0) return;

    pushEvent({ zipMapFields });
}

// ─── search / filter / sort ──────────────────────────────────────────────────

function createFbEventId(): number {
    return Math.floor(Math.random() * 9_000_000_000) + 1_000_000_000;
}

/**
 * Pushes search event to dataLayer when user performs a search.
 * Includes fbEventId for Facebook pixel tracking (legacy parity).
 */
export function pushSearch(query: string): void {
    const pageContext = getPageContext();
    pushEvent({
        event: 'search',
        event_name: 'search',
        searchString: query,
        fbEventId: createFbEventId(),
        ...pageContext,
    });
}

export interface FilterClickData {
    facetName: string;
    facetValue: string;
}

export interface FilterClickOptions {
    /** When false, suppress the legacy camelCase (`filterClick`) alias push. Default true. */
    emitLegacyAliases?: boolean;
}

/**
 * Pushes the GA4-canonical `filter_click` event AND, unless
 * `emitLegacyAliases === false`, a second legacy `filterClick` (camelCase)
 * push for GA3 tag continuity.
 */
export function pushFilterClick(data: FilterClickData, options: FilterClickOptions = {}): void {
    pushEvent({
        event: 'filter_click',
        event_name: 'filter_click',
        sort_rule: data.facetValue,
        facet: data.facetValue,
        facet_header: data.facetName,
        facetHeader: data.facetName,
    });

    if (options.emitLegacyAliases === false) return;
    pushEvent({
        event: 'filterClick',
        sort_rule: data.facetValue,
        facet: data.facetValue,
        facetHeader: data.facetName,
    });
}

export interface SortClickOptions {
    /** When false, suppress the legacy camelCase (`sortClick`) alias push. Default true. */
    emitLegacyAliases?: boolean;
}

/**
 * Pushes the GA4-canonical `sort_click` event AND, unless
 * `emitLegacyAliases === false`, a second legacy `sortClick` (camelCase)
 * push for GA3 tag continuity.
 */
export function pushSortClick(sortRule: string, options: SortClickOptions = {}): void {
    pushEvent({
        event: 'sort_click',
        event_name: 'sort_click',
        sort_rule: sortRule,
    });

    if (options.emitLegacyAliases === false) return;
    pushEvent({
        event: 'sortClick',
        sort_rule: sortRule,
    });
}

// ─── created_lead ────────────────────────────────────────────────────────────

export type CreatedLeadSource = 'create account' | 'checkout' | 'newsletter_signup';

export interface CreatedLeadData {
    /** Source of lead creation: signup page or checkout guest registration */
    widget?: CreatedLeadSource;
    /** Element ID for GTM: 'Email', 'Mobile', 'Email & Mobile', or default */
    elementId?: string;
    /** Whether the user opted in to marketing communications */
    marketingConsent?: boolean;
}

/**
 * Pushes created_lead event to dataLayer.
 * Use when user creates an account (signup or guest registration at checkout).
 * Uses event: "nonInteraction" for legacy GTM compatibility.
 * Does not include PII; server-side hashing is not available client-side.
 */
export function pushCreatedLead(data: CreatedLeadData = {}): void {
    const pageContext = getPageContext();
    pushEvent({
        event: 'nonInteraction',
        event_name: 'created_lead',
        element_id: data.elementId ?? 'create account',
        widget: data.widget ?? 'create account',
        marketing_consent: data.marketingConsent ?? false,
        ...pageContext,
    });
}

// ─── wishlist ────────────────────────────────────────────────────────────────

export interface WishlistEventData {
    /** Product identifier — required. Used to build the minimal item[] entry when no full product is supplied. */
    productId: string;
    /** Optional human-readable name for the minimal item[] entry. */
    productName?: string;
    /**
     * Optional full product/basket ProductItem shape. When supplied, the GA4
     * item is built via `buildBasketItem` / `buildEcommerceItem` so all the
     * Ashley named-parameter customs propagate onto the wishlist event.
     */
    product?: ProductLike;
    /** Optional cart context to thread onto the item's cart_id / cart_type. */
    cartScope?: { cartId?: string; cartType?: string };
}

export interface WishlistLegacyAliasOptions {
    /** When false, suppress the UA/legacy `event: 'trackEvent'` twin push. Default true. */
    emitLegacyAliases?: boolean;
}

function buildWishlistItem(data: WishlistEventData): EcommerceItem {
    if (data.product) {
        // Basket ProductItem shape includes `itemId` — route through
        // `buildBasketItem` for that path so quantity defaults to the line's
        // own quantity. Otherwise use `buildEcommerceItem` with quantity 1.
        const asItem = data.product as unknown as ProductItem;
        if (typeof asItem.itemId === 'string' && asItem.itemId.length > 0) {
            return buildBasketItem(asItem, { cartScope: data.cartScope, eventScope: 'cart' });
        }
        return buildEcommerceItem(data.product, 1, undefined, { cartScope: data.cartScope, eventScope: 'cart' });
    }
    // Minimal item when only the productId (and optionally productName) is known.
    return {
        item_id: data.productId,
        item_name: data.productName ?? '',
    };
}

/**
 * Pushes GA4-standard `add_to_wishlist` (and, unless `emitLegacyAliases === false`,
 * the legacy `trackEvent` UA twin — matches legacy tagmanager.js:
 *   pushEvent('trackEvent', 'Wishlist', 'Add', productID)).
 */
export function pushWishlistAdd(data: string | WishlistEventData, options: WishlistLegacyAliasOptions = {}): void {
    const eventData: WishlistEventData = typeof data === 'string' ? { productId: data } : data;
    const item = buildWishlistItem(eventData);

    // GA4 canonical.
    pushEcommerceEvent(
        'add_to_wishlist',
        {
            items: [item],
        },
        {},
        'nonInteraction'
    );

    if (options.emitLegacyAliases === false) return;

    // Legacy UA twin — kept intact for GA3 tag continuity.
    pushEvent({
        event: 'trackEvent',
        eventCategory: 'Wishlist',
        eventAction: 'Add',
        eventLabel: eventData.productId,
    });
}

/**
 * Pushes GA4-standard `remove_from_wishlist` (and, unless `emitLegacyAliases === false`,
 * the legacy `trackEvent` UA twin — matches legacy tagmanager.js:
 *   pushEvent('trackEvent', 'Wishlist', 'Remove', productID)).
 */
export function pushWishlistRemove(data: string | WishlistEventData, options: WishlistLegacyAliasOptions = {}): void {
    const eventData: WishlistEventData = typeof data === 'string' ? { productId: data } : data;
    const item = buildWishlistItem(eventData);

    pushEcommerceEvent(
        'remove_from_wishlist',
        {
            items: [item],
        },
        {},
        'nonInteraction'
    );

    if (options.emitLegacyAliases === false) return;

    pushEvent({
        event: 'trackEvent',
        eventCategory: 'Wishlist',
        eventAction: 'Remove',
        eventLabel: eventData.productId,
    });
}
